import { useState, useCallback, useRef, useEffect } from "react";
import { RootState } from "redux/store";
import { Button } from "react-bootstrap";
import { Modal, Table } from "components";
import { getRowIds } from "helpers/data/dataHandling";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { Manufacturers } from "popups/Manufacturers/Manufacturers";
import { addAGGridSelectedManufacturersRows, addAGGridSelectedRows, updatePreferredBrandField } from "redux/slices/PreferredBrands";
import { getPbsManufacturers } from "redux/slices/PbsManufacturers";
import { getPreferredBrandDetails } from "redux/slices/PreferredBrands";
import { AgGridReact } from "ag-grid-react";
import BaseCellRenderer from "components/AgGrid/CellRenderers/BaseCellRenderer";
import { getPreferredBrandsManufacturerConfig } from "TableConfigs/PreferredBrandsManufacturerConfig";
import { ProductPbsManufacturer } from "models/dbModels/products/ProductPbsManufacturer";
import { AgGridToolbar } from "components/AgGrid/AgGridToolbar";
import { Z } from "models/common/components";
import { ZTooltip } from "components/ZTooltip";
import { ZIconType } from "helpers/utility/iconMap";

export function Manufacturer() {
    const dispatch = useAppDispatch();
    const manufacturers = useAppSelector((state: RootState) => state.pbsManufacturers);
    const [showPopup, setShowPopup] = useState(false);
    const preferredBrands = useAppSelector((state: RootState) => state.preferredBrands);
    const preferredBrandsManufacturersGridRef = useRef<AgGridReact>(null);
    const [productPbsManufacturerData, setProductPbsManufacturerData] = useState<ProductPbsManufacturer[]>([]);
    const [showDelete, setShowDelete] = useState<boolean>(false);

    useEffect(()=>{
        setProductPbsManufacturerData(getEditableProductPbsManufacturer());
    },[preferredBrands.pbsManufacturers])

    function getEditableProductPbsManufacturer(): ProductPbsManufacturer[]{
        let newData: ProductPbsManufacturer[] = [];
        preferredBrands.pbsManufacturers.forEach((row: ProductPbsManufacturer) => {
             const prefBrands:  ProductPbsManufacturer = {
                 pbsManufacturerId: row.pbsManufacturerId,
                 code: row.code,
                 name: row.name,
                 rank: row.rank
             };

             newData.push(prefBrands);
        })

        return newData;
    }
   
    async function manufacturersOnSave() {
        let selectedManufacturers: any[] = [];
        let deseledManufacturers: any[] = [];
        let preferredBrandsManufacturersList: any[] = [...preferredBrands.pbsManufacturers];
        const preferredBrandsManufacturersListIds: any[] = [...preferredBrands.pbsManufacturers.map((manufacturer: any) => manufacturer.pbsManufacturerId)];
        let manufacturersList: any[] = [];
        selectedManufacturers = getRowIds(manufacturers.selectedRows);
        deseledManufacturers = getRowIds(manufacturers.deselectedRows);

        await dispatch(getPbsManufacturers({})).then((res) => manufacturersList = res.data);

        if (manufacturers.selectAllFilter) {
            manufacturersList.forEach((manufacturer: any) => {
                if (!preferredBrandsManufacturersListIds.includes(manufacturer.id) && !deseledManufacturers.includes(manufacturer.id)) {
                    preferredBrandsManufacturersList.push({
                        pbsManufacturerId: manufacturer.id,
                        rank: 0,
                        code: manufacturer.code,
                        name: manufacturer.name,
                    });
                }
            });
        } else {
            selectedManufacturers.forEach((manufacturer: any) => {
                if (!preferredBrandsManufacturersListIds.includes(manufacturer)) {
                    const manufacturerFilterted = manufacturersList.filter((manufacturerItem: any) => manufacturerItem.id === manufacturer);
                    if (manufacturerFilterted.length > 0) {
                        preferredBrandsManufacturersList.push({
                            pbsManufacturerId: manufacturerFilterted[0].id,
                            rank: 0,
                            code: manufacturerFilterted[0].code,
                            name: manufacturerFilterted[0].name,
                        });
                    }
                }
            });
        }
        const preferredBrandsManufacturersIndexes = preferredBrandsManufacturersList.map((manufacturer: any, index: number) => ({ ...manufacturer, rank: index + 1 }));
        dispatch(updatePreferredBrandField({ key: "pbsManufacturers", value: preferredBrandsManufacturersIndexes }));
        setShowPopup(false);
    }

    function handleDelete() {
        let selectedManufacturersIds: string[] | undefined = preferredBrandsManufacturersGridRef.current?.api.getSelectedRows().map((row: any) => row.pbsManufacturerId);      
        let remainingManufacturers = preferredBrands.pbsManufacturers.filter((xe)=> !selectedManufacturersIds?.includes(xe.pbsManufacturerId));
        dispatch(updatePreferredBrandField({ key: "pbsManufacturers", value: remainingManufacturers }));
    }

    function updateRedux(){
        let rebuiltRows: ProductPbsManufacturer[] = [];
        preferredBrandsManufacturersGridRef.current?.api.forEachNode((node) => {rebuiltRows.push(node.data)});
        dispatch(updatePreferredBrandField({ key: "pbsManufacturers", value: rebuiltRows }));
    }

    return (
        <>
            <Manufacturers show={showPopup} onClose={() => setShowPopup(false)} onSave={manufacturersOnSave} />
            <AgGridToolbar
                search={true}
                className="pb-1"
                handleSubmit={(searchState, filterState) => {
                    preferredBrandsManufacturersGridRef.current?.api.clearRangeSelection();
                    dispatch(addAGGridSelectedRows([]));
                    preferredBrandsManufacturersGridRef.current?.api.setGridOption('quickFilterText', searchState?.keyword);                                            
                }}
                searchProps={
                    {totalRows: preferredBrands.pbsManufacturers.length, placeholder: "by Name", hideContains: true}
                }
                children={(
                    <div className="d-inline-flex gap-1">
                        { 
                        (preferredBrands.selectedManufacturersRows != undefined && preferredBrands.selectedManufacturersRows.length != 0) &&									
                            <Z.Button variant="light" className="h-100" disabled={true} buttonText={preferredBrands.selectedManufacturersRows.length+ " Selected"}/>
                        }
                            <ZTooltip text="Add">
                            <Z.Button icon={ZIconType.Add} buttonSize="md" onClick={() => setShowPopup(true)}/>
                            </ZTooltip>
                        <ZTooltip text={
                            preferredBrands.selectedManufacturersRows.length == 0? `Please select a row to delete`:
                            preferredBrands.selectedManufacturersRows.length == preferredBrands.pbsManufacturers.length ? `Delete all rows`:									  
                            preferredBrands.selectedManufacturersRows.length> 0 ? `Delete ${preferredBrands.selectedManufacturersRows.length} rows` :                                                    
                            "Please select a row to delete"}>
                            <Z.Button icon={ZIconType.Delete} buttonSize="md"  onClick={() => setShowDelete(true)} 
                                disabled={preferredBrands.selectedManufacturersRows.length < 1} />
                        </ZTooltip>
                        <ZTooltip text="Refresh">
                            <Z.Button icon={ZIconType.Refresh} buttonSize="md" onClick={() => 
                                dispatch(getPreferredBrandDetails({ ids: [preferredBrands.id], loadDetails: ['Manufacturers', 'Stock', 'Stores']  })).then((res) => {
                                    if(res.totalRows == 0){
                                        preferredBrandsManufacturersGridRef.current?.api.showNoRowsOverlay()
                                    }
                                })} 
                            />
                        </ZTooltip>
                    </div>
                )}
            />
            <AgGridReact
                ref={preferredBrandsManufacturersGridRef}
                columnDefs={getPreferredBrandsManufacturerConfig(productPbsManufacturerData.length, () => updateRedux())}
                rowData={productPbsManufacturerData}
                pagination={true}
                paginationPageSize={20}
                className="ag-theme-quartz ag-theme-custom w-100"
                containerStyle={{ height: "65dvh" }}
                rowSelection="multiple"
                suppressRowClickSelection={true}
                suppressCellFocus={true}
                suppressContextMenu
                onSelectionChanged={(e) => {
                    dispatch(addAGGridSelectedManufacturersRows(e.api.getSelectedRows().map((xe: any) =>  xe.id)));
                }}
                onRowSelected={(e) => {
                    dispatch(addAGGridSelectedManufacturersRows(e.api.getSelectedRows().map((xe: any) =>  xe.id)));
                }}
                components={{
                    baseCellRenderer: BaseCellRenderer,
                }}
            />
            <Modal
                title="Confirm Delete"
                show={showDelete}
                buttonType="primary"
                buttonText={"Continue"}
                buttonCloseText="Cancel"
                handleSubmit={() => {
                    handleDelete();
                    setShowDelete(false);
                    preferredBrandsManufacturersGridRef.current?.api.deselectAll();

                }}
                handleClose={() => setShowDelete(false)}
                description={
                    <>
                        <p>You are about to delete {preferredBrands.selectedManufacturersRows.length} selected {preferredBrands.selectedManufacturersRows.length > 1 ? "items": "item"}.</p>
                        <p>Are you sure you want to delete?</p>
                    </>
                }
            />
        </>
    );
}